import { logger } from '@/utils';
import { autobind, Injectable, Inject } from '@kaokei/use-vue-service';
// import { DaoService } from '../dao.service';
import { DeviceManager } from '@/utils';
import { DeviceErrorDescription } from '@/utils/device/error';
import { IError } from '@/utils/device/interface';
import { checkBrowser } from '@/utils/check';
import { useRoute, useRouter } from 'vue-router';
import { DbRecord } from '@/utils/recorderTools/dbRecord';
import { message } from 'ant-design-vue';
import moment from 'moment';
import { ShowAlertModal } from '@/views/examination/common/dialog';
import { UserService } from '../user.service';

const delayFn = (time = 2) => {
  // 每次检测至少10s以上，否则显得不够真实
  return new Promise(resolve => {
    window.setTimeout(() => resolve(true), time * 1000);
  });
};

const DeviceStateData = () => ({
  hasClickChecked: false,
  browser: {
    status: 'wait',
    txt: '设备待检测',
    deviceList: [],
  },
  camera: {
    status: 'wait',
    txt: '设备待检测',
    deviceList: [],
  },
  micro: {
    status: 'wait',
    txt: '设备待检测',
    deviceList: [],
  },
  screen: {
    status: 'wait',
    txt: '待分享屏幕',
    deviceList: [],
  },
  recordType: {
    status: 'wait',
    txt: '待检测',
    deviceList: [],
  },
});

@Injectable()
export class ScreenMediaService {
  @Inject(UserService)
  public userService!: UserService;

  route: any = useRoute();
  router: any = useRouter();
  dbRecord: DbRecord;
  deviceManager?: any;
  chunks: Blob[] = [];
  recordDuration = 0;
  recordInterval: NodeJS.Timeout | null = null;
  recordStartTime = 0;
  recordEndTime = 0;
  totalRecordDuration = 0;
  public mediaRecorder: MediaRecorder | null = null;
  public stream?: MediaStream; // 视频流和音频流
  public screenStream?: MediaStream; // 视频流和音频流
  public mediaStream?: MediaStream; // 视频流和音频流
  public error?: Event; // 错误信息
  // 设备检测状态 active | inactive
  public deviceActiveStatus = '';
  public blobUrl: string | null = null;
  public blobData: Blob | null = null;
  public videoPreviewTimer: NodeJS.Timeout | null = null;
  public recordStatus = ''; // 录制状态 start | pause
  public videoPreviewConfig = {
    spinning: false,
    // 视频录制最大时间
    recordMaxTime: 60,
    isMoreThanMaxTime: false,
    showPreview: false,
    // 纯url预览标识
    urlPreview: false,
  };

  public constraints = {
    audio: true,
    video: {
      width: { min: 640, ideal: 640, max: 640 },
      height: { min: 480, ideal: 480, max: 480 },
      framerate: 60,
    },
  };

  // 检测浏览器的结果
  public checkBrowserResult: any;

  public DeviceState: any = DeviceStateData();

  // 考试试卷ID
  public examId = -1;

  constructor() {
    // 获取单例实例
    this.dbRecord = DbRecord.getInstance();
    this.deviceManager = new DeviceManager();
  }

  public get isRecording() {
    return this.recordStatus === 'start';
  }

  public get recordFormatTime() {
    return this.dealFormatTime(this.recordDuration);
  }

  public get recordTotalFormatTime() {
    return this.dealFormatTime(this.videoPreviewConfig.recordMaxTime);
  }

  public get isLessThan30Seconds() {
    return this.videoPreviewConfig.recordMaxTime - this.recordDuration < 30;
  }

  public get userExamId() {
    return this.route.params?.id ?? -1;
  }

  public setExamId(id: number) {
    this.examId = id;
  }

  public dealFormatTime(time: number) {
    const duration = moment.duration(time || 0, 'seconds');
    // 获取小时、分钟和秒数
    const minutes = duration.minutes().toString().padStart(2, '0');
    const secs = duration.seconds().toString().padStart(2, '0');
    // return `${hours}:${minutes}:${secs}`;
    return `${minutes}:${secs}`;
  }

  @autobind
  public showVideoPreview(flag = true) {
    this.videoPreviewConfig.showPreview = flag ?? true;
  }

  @autobind
  public initCheckState() {
    this.DeviceState = DeviceStateData();
    this.recordDuration = 0;
    this.totalRecordDuration = 0;
  }

  @autobind
  public setVideoRecordMaxTime(time: number) {
    this.videoPreviewConfig.recordMaxTime = time;
  }

  @autobind
  public initRecordState() {
    this.recordDuration = 0;
  }

  /**
   * 生成预览视频
   */
  @autobind
  public createPreviewVideo() {
    this.videoPreviewConfig.spinning = true;
    this.videoPreviewTimer = setTimeout(() => {
      this.getRecordCacheData();
      this.showVideoPreview(true);
      this.videoPreviewConfig.spinning = false;
    }, 1000);
  }

  @autobind
  public getChromeVersion() {
    const userAgent = navigator.userAgent;
    const chromeVersionMatch = userAgent.match(/Chrome\/(\d+\.\d+\.\d+\.\d+)/);
    if (chromeVersionMatch) {
      return {
        status: 200,
        version: `Chrome内核版本：${chromeVersionMatch[1]}`,
      };
    } else {
      return {
        status: 0,
        version: '非Chrom内核浏览器',
      };
    }
  }

  @autobind
  public async checkBrowser() {
    const { DeviceState } = this;
    await delayFn(1);
    const result = this.getChromeVersion();
    DeviceState.browser.status = result.status === 200 ? 'finish' : 'error';
    DeviceState.browser.txt = result.version;
  }

  public async checkVideo() {
    const { DeviceState } = this;
    DeviceState.camera.status = 'process';
    await delayFn(1);
    return new Promise((resovle, reject) => {
      this.deviceManager
        .getCameraList()
        .then((list: any) => {
          DeviceState.camera.status = 'finish';
          DeviceState.camera.deviceList = list;
          DeviceState.camera.txt = list[0].label;
          this.canDeviceCheckTotal();
          resovle(true);
        })
        .catch((e: IError) => {
          DeviceState.camera.txt = e.reason || '~未找到设备~';
          DeviceState.camera.status = 'error';
          reject(e);
        });
    });
  }

  @autobind
  public async checkAudio() {
    const { DeviceState } = this;
    DeviceState.micro.status = 'process';
    await delayFn(1);
    return new Promise((resovle, reject) => {
      this.deviceManager
        .getMicList()
        .then((list: any) => {
          DeviceState.micro.deviceList = list;
          DeviceState.micro.status = 'finish';
          DeviceState.micro.txt = list[0].label;
          this.canDeviceCheckTotal();
          resovle(true);
        })
        .catch((e: IError) => {
          DeviceState.micro.txt = e.reason || '~未找到设备~';
          DeviceState.micro.status = 'error';
          reject(e);
        });
    });
  }

  @autobind
  public async canDeviceCheckTotal() {
    const { DeviceState } = this;
    if (
      DeviceState.camera.status === 'finish' &&
      DeviceState.micro.status === 'finish'
    ) {
      /* eslint-disable no-console */
      try {
        await this.getMediaStream();
        DeviceState.camera.status = 'finish';
        DeviceState.micro.status = 'finish';
      } catch (e) {
        if (e instanceof Error) {
          const errResult = this.parseErrorMsg(e);
          if (errResult) {
            DeviceState.camera.txt = errResult.message;
            DeviceState.camera.status = 'error';
            DeviceState.micro.txt = errResult.message;
            DeviceState.micro.status = 'error';
          }
        }
      }
      /* eslint-enable no-console */
    }
  }

  @autobind
  // 其他需要检测的明细
  public getCheckBrowserResult() {
    const result = checkBrowser();
    this.checkBrowserResult = result;
    return result;
  }

  /**
   * 开始检测设备
   * @returns
   */
  @autobind
  public checkStartDevice() {
    return new Promise((resolve, reject) => {
      const { DeviceState } = this;
      DeviceState.browser.status = 'process';
      DeviceState.micro.status = 'process';
      DeviceState.camera.status = 'process';
      const asyncCheckStart = async () => {
        try {
          await Promise.all([
            this.checkBrowser(),
            this.getMediaStream(), // catch 异常
            this.getCheckBrowserResult(), // 一定成功
            delayFn(1),
          ]);
          const list = await this.getDeviceList();
          DeviceState.micro.deviceList = [];
          DeviceState.camera.deviceList = [];
          list.forEach((el: MediaDeviceInfo) => {
            if (el.kind === 'audioinput') {
              DeviceState.micro.deviceList.push(el);
            }
            if (el.kind === 'videoinput') {
              DeviceState.camera.deviceList.push(el);
            }
          });
          if (this.stream) {
            if (DeviceState.micro.status !== 'error') {
              DeviceState.micro.status = 'finish';
              DeviceState.micro.txt = DeviceState.micro.deviceList[0]?.label;
            }
            if (DeviceState.camera.status !== 'error') {
              DeviceState.camera.status = 'finish';
              DeviceState.camera.txt = DeviceState.camera.deviceList[0]?.label;
            }
          } else {
            DeviceState.micro.status = 'error';
            DeviceState.micro.txt ||= '未知异常';
            DeviceState.camera.status = 'error';
            DeviceState.camera.txt ||= '未知异常';
          }
          // 状态置为已点击检测
          DeviceState.hasClickChecked = true;
          resolve(true);
        } catch (e: any) {
          // 状态置为已点击检测
          DeviceState.hasClickChecked = true;
          reject(e);
        }
      };
      asyncCheckStart();
    });
  }

  @autobind
  public async checkMediaRecorderType() {
    const { DeviceState } = this;
    DeviceState.recordType.status = 'process';
    await delayFn(1);
    const [, containerType] = this.getMediaRecorderOptions();
    if (containerType === 'video/mp4') {
      DeviceState.recordType.status = 'finish';
      DeviceState.recordType.txt = `.mp4`;
    } else {
      DeviceState.recordType.status = 'error';
      DeviceState.recordType.txt = '不支持录制MP4';
    }
  }

  @autobind
  public checkStartScreen() {
    return new Promise((resolve, reject) => {
      const { DeviceState } = this;
      DeviceState.recordType.status = 'process';
      DeviceState.screen.status = 'process';
      const asyncCheckStart = async () => {
        try {
          await Promise.all([
            this.checkMediaRecorderType(),
            this.getScreenStream(), // catch 异常
            delayFn(1),
          ]);
          resolve(true);
        } catch (e: any) {
          logger.error(e);
          reject(e);
        }
      };
      asyncCheckStart();
    });
  }

  @autobind
  public getDeviceList(): Promise<Array<MediaDeviceInfo>> {
    return new Promise((resolve, reject) => {
      navigator.mediaDevices
        .enumerateDevices()
        .then(deviceList => {
          const arr: Array<MediaDeviceInfo> = [];
          deviceList.forEach((device: MediaDeviceInfo) => {
            arr.push(device);
          });
          resolve(arr);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  @autobind
  public getScreenStream() {
    this.resetScreenStream();
    const { DeviceState } = this;
    DeviceState.screen.status = 'process';
    return new Promise((resolve, reject) => {
      navigator.mediaDevices
        .getDisplayMedia({
          video: true,
          audio: false, // 可选：捕获标签页内音频（如播放中的音乐）
        })
        .then(mstream => {
          this.screenStream = mstream;
          DeviceState.screen.txt = '已分享';
          DeviceState.screen.status = 'finish';
          // const [track] = mstream.getVideoTracks();
          // const obj: any = track.getSettings();
          // console.log('用户共享类型：', obj.displaySurface); // 输出 monitor/window/browser
          mstream.addEventListener('inactive', this.screenInactiveHandler);
          mstream.getTracks().forEach(track => {
            if (track.kind == 'video') {
              track.onended = event => {
                try {
                  window.hetaoTrack('element_show', {
                    userId: this.userService?.user?.userId || 0,
                    uniqueId: this.userExamId,
                    elementName: '异常中断弹窗',
                    statusText: '由于屏幕录制中断导致录制异常结束',
                    examId: this.examId,
                  });
                  // eslint-disable-next-line no-empty
                } catch (e) {}
                ShowAlertModal({
                  title: '中断提示',
                  content: '由于屏幕录制中断导致录制异常结束',
                  okLoading: this.videoPreviewConfig.spinning,
                  okText: '生成预览视频',
                  type: 'warning',
                }).then(() => {
                  this.createPreviewVideo();
                });
                this.dispose();
              };
            }
          });
          resolve(mstream);
        })
        .catch(e => {
          logger.error(e);
          if (e instanceof Error) {
            const errResult = this.parseErrorMsg(e);
            if (errResult) {
              DeviceState.screen.txt = errResult.message;
            }
          }
          DeviceState.screen.status = 'error';
          resolve(null);
        });
    });
  }

  @autobind
  public getMediaStream() {
    this.resetStream();
    const { DeviceState } = this;
    return new Promise((resolve, reject) => {
      navigator.mediaDevices
        .getUserMedia(this.constraints)
        .then(mstream => {
          this.stream = mstream;

          mstream.getTracks().forEach(track => {
            if (track.kind == 'audio') {
              track.onended = event => {
                this.error = event;
                try {
                  window.hetaoTrack('element_show', {
                    userId: this.userService?.user?.userId || 0,
                    uniqueId: this.userExamId,
                    elementName: '异常中断弹窗',
                    statusText: '由于音频录制中断导致录制异常结束',
                    examId: this.examId,
                  });
                  // eslint-disable-next-line no-empty
                } catch (e) {}
                ShowAlertModal({
                  title: '中断提示',
                  content: '由于音频录制中断导致录制异常结束',
                  okLoading: this.videoPreviewConfig.spinning,
                  okText: '生成预览视频',
                  type: 'warning',
                }).then(() => {
                  this.createPreviewVideo();
                });
                this.dispose();
              };
            }
            if (track.kind == 'video') {
              track.onended = event => {
                this.error = event;
                try {
                  window.hetaoTrack('element_show', {
                    userId: this.userService?.user?.userId || 0,
                    uniqueId: this.userExamId,
                    elementName: '异常中断弹窗',
                    statusText: '由于摄像头录制中断导致录制异常结束',
                    examId: this.examId,
                  });
                  // eslint-disable-next-line no-empty
                } catch (e) {}
                ShowAlertModal({
                  title: '中断提示',
                  content: '由于摄像头录制中断导致录制异常结束',
                  okLoading: this.videoPreviewConfig.spinning,
                  okText: '生成预览视频',
                  type: 'warning',
                }).then(() => {
                  this.createPreviewVideo();
                });
                this.dispose();
              };
            }
          });

          this.deviceActiveStatus = 'active';
          this.stream.addEventListener('inactive', this.msInactiveHandler);
          try {
            window.hetaoTrack('element_show', {
              elementName: '赛事录屏检测状态',
              statusText: 'success',
              live_status: 'success',
              userId: this.userService?.user?.userId || 0,
              uniqueId: this.userExamId,
              examId: this.examId,
            });
            // eslint-disable-next-line no-empty
          } catch (e) {}
          resolve(mstream);
        })
        .catch(e => {
          if (e instanceof Error) {
            const errResult = this.parseErrorMsg(e);
            if (errResult) {
              DeviceState.camera.txt = errResult.message;
              DeviceState.camera.status = 'error';
              DeviceState.micro.txt = errResult.message;
              DeviceState.micro.status = 'error';
            }
          }
          try {
            window.hetaoTrack('element_show', {
              elementName: '赛事录屏检测状态',
              statusText: e?.message || 'error',
              live_status: 'error',
              userId: this.userService?.user?.userId || 0,
              uniqueId: this.userExamId,
              examId: this.examId,
            });
            // eslint-disable-next-line no-empty
          } catch (e) {}
          resolve(null);
        });
    });
  }

  @autobind
  public getMediaRecorderOptions() {
    let options: MediaRecorderOptions | undefined;
    let containerType = 'video/webm';

    if (typeof MediaRecorder.isTypeSupported == 'function') {
      if (
        MediaRecorder.isTypeSupported(
          'video/mp4; codecs="avc1.424028, mp4a.40.2"'
        )
      ) {
        containerType = 'video/mp4';
        options = { mimeType: 'video/mp4; codecs="avc1.424028, mp4a.40.2"' };
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
        containerType = 'video/mp4';
        options = { mimeType: 'video/mp4' };
      }
    }

    return [options, containerType];
  }

  @autobind
  public async prepareRecording() {
    return new Promise((resolve, reject) => {
      const handleAsync = async () => {
        try {
          let screenStream: any;
          let stream: any;
          if (this.screenStream) {
            screenStream = this.screenStream;
            stream = this.stream;
          } else {
            screenStream = await this.getScreenStream();
            stream = await this.getMediaStream();
          }
          if (!screenStream || !stream) {
            return reject('没有获取到视频流');
          }
          const [options] = this.getMediaRecorderOptions();
          const combinedStream = new MediaStream([
            ...screenStream.getVideoTracks(),
            ...stream.getAudioTracks(),
          ]);
          this.mediaStream = combinedStream;
          this.mediaRecorder = options
            ? new MediaRecorder(combinedStream, options as MediaRecorderOptions)
            : new MediaRecorder(combinedStream);
          this.mediaRecorder?.addEventListener('dataavailable', e => {
            // this.chunks.push(e.data);
            logger.info('dataavailable', e.data.size);
            // 添加记录
            this.dbRecord.addRecord({
              data: e.data,
              recordFormatTime: this.recordFormatTime,
            });
          });
          this.mediaRecorder?.addEventListener('error', e => {
            message.error('录制错误，请检查浏览器是否支持录制功能');
          });
          this.mediaRecorder?.addEventListener('start', () => {
            this.recordStatus = 'start';
            this.recordStartTime = Date.now();
            this.recordInterval = setInterval(() => {
              this.recordDuration = Math.floor(
                (Date.now() - this.recordStartTime) / 1000
              );
              this.maxTimeJudge();
            }, 1000);
          });
          this.mediaRecorder?.addEventListener('pause', () => {
            this.recordStatus = 'pause';
            if (this.recordInterval) {
              clearInterval(this.recordInterval);
              this.recordInterval = null;
            }
            this.recordDuration = Math.floor(
              (Date.now() - this.recordStartTime) / 1000 +
                this.totalRecordDuration
            );
            this.totalRecordDuration = this.recordDuration;
          });
          this.mediaRecorder?.addEventListener('resume', () => {
            this.recordStatus = 'start';
            this.recordStartTime = Date.now();
            this.recordInterval = setInterval(() => {
              this.recordDuration = Math.floor(
                (Date.now() - this.recordStartTime) / 1000 +
                  this.totalRecordDuration
              );
              this.maxTimeJudge();
            }, 1000);
          });
          this.mediaRecorder?.addEventListener('stop', () => {
            if (this.recordInterval) {
              clearInterval(this.recordInterval);
              this.recordInterval = null;
            }
            // 开始录制的时候才能计算
            if (this.recordStatus === 'start') {
              this.recordDuration = Math.floor(
                (Date.now() - this.recordStartTime) / 1000 +
                  this.totalRecordDuration
              );
            }
            logger.info(this.totalRecordDuration, this.recordDuration);
            // 清除录制流
            this.mediaStream?.getTracks().forEach(track => track.stop());
            // 视频流清除
            this.stream?.getTracks().forEach(track => track.stop());
            // 屏幕流清除
            this.screenStream?.getTracks().forEach(track => track.stop());
            // 初始化 清空的数据
            this.mediaStream = undefined;
            this.stream = undefined;
            this.screenStream = undefined;
          });
          resolve(true);
        } catch (e) {
          logger.error(e);
          reject(e);
        }
      };
      handleAsync();
    });
  }

  maxTimeJudge() {
    if (this.videoPreviewConfig.recordMaxTime > 0) {
      if (this.recordDuration >= this.videoPreviewConfig.recordMaxTime) {
        this.videoPreviewConfig.isMoreThanMaxTime = true;
      }
    }
  }

  // 开始录制
  start() {
    this.videoPreviewConfig.isMoreThanMaxTime = false;
    // 清空上一次的录制记录
    this.dbRecord.clearRecords();
    // 每1秒录制一次
    this.mediaRecorder?.start(1000);
  }

  stop() {
    this.mediaRecorder?.stop();
  }

  pause() {
    logger.info('测试 暂停录制');
    this.mediaRecorder?.pause();
  }

  resume() {
    logger.info('测试resume');
    this.mediaRecorder?.resume();
  }

  async getRecordCacheData() {
    const chunks = await this.dbRecord.getCacheChunks();
    const blob = new Blob(chunks, {
      type: 'video/mp4',
    });
    const url = URL.createObjectURL(blob);
    this.blobUrl = url;
    this.blobData = blob;
  }

  @autobind
  public msInactiveHandler() {
    this.deviceActiveStatus = 'inactive';
    window.hetaoTrack('element_show', {
      elementName: '赛事录屏检测状态',
      statusText: 'inactive',
      live_status: 'inactive',
      userId: this.userService?.user?.userId || 0,
      uniqueId: this.userExamId,
      examId: this.examId,
      event_type: 'media',
    });
  }

  @autobind
  public screenInactiveHandler() {
    this.deviceActiveStatus = 'inactive';
    window.hetaoTrack('element_show', {
      elementName: '赛事录屏检测状态',
      statusText: 'inactive',
      live_status: 'inactive',
      userId: this.userService?.user?.userId || 0,
      uniqueId: this.userExamId,
      examId: this.examId,
      event_type: 'screen',
    });
  }

  @autobind
  public parseErrorMsg(err: Error) {
    if (
      err.message === DeviceErrorDescription.ERRORMESSAGE_DEVICEDISMISSED ||
      err.name === DeviceErrorDescription.ERRORMESSAGE_DEVICEDISMISSED
    ) {
      return {
        message: '权限未被允许或被驳回',
        otherMsg: '关闭了权限弹窗',
      };
    } else if (
      err.message === DeviceErrorDescription.ERRORMESSAGE_DEVICENOTFOUND ||
      err.name === DeviceErrorDescription.ERRORNAME_DEVICENOTFOUND
    ) {
      return {
        message: '未找到相关设备',
        otherMsg: '',
      };
    } else if (
      err.message === DeviceErrorDescription.ERRORMESSAGE_DEVICENOTALLOWED ||
      err.message ===
        DeviceErrorDescription.ERRORMESSAGE_MOBILEDEVICE_NOTALLOWED ||
      err.message ===
        DeviceErrorDescription.ERRORMESSAGE_MACSAFARI_DEVICENOTALLOWED
    ) {
      return {
        message: '不允许使用相关设备',
        otherMsg: '权限允许弹窗被拒绝，或被拒绝过',
      };
    } else if (
      err.message ===
        DeviceErrorDescription.ERRORMESSAGE_MACCHROME_DEVICENOTREADABLE ||
      err.message ===
        DeviceErrorDescription.ERRORMESSAGE_VIDEODEVICENOTREADABLE ||
      err.name === DeviceErrorDescription.ERRORNAME_DEVICENOTREADABLE
    ) {
      return {
        message: '没有使用相关设备权限',
        otherMsg:
          '设备硬件错误，或浏览器被系统禁止权限，如Mac-安全性与隐私-隐私-麦克风',
      };
    } else if (
      err.name === DeviceErrorDescription.ERRORNAME_DEVICEOVERCONSTRAINED
    ) {
      return {
        message: '无法满足当前MediaStreamTrack的所需功能集',
        otherMsg: '',
      };
    }
    return null;
  }

  @autobind
  public resetStream() {
    if (this.stream) {
      this.stream.removeEventListener('inactive', this.msInactiveHandler);
    }
    this.deviceActiveStatus = '';
    this.stream = undefined;
    this.error = undefined;
  }

  @autobind
  public resetScreenStream() {
    if (this.screenStream) {
      this.screenStream.removeEventListener('inactive', this.msInactiveHandler);
    }
    this.recordStatus = '';
    this.deviceActiveStatus = '';
    this.screenStream = undefined;
  }

  @autobind
  public dispose() {
    if (this.recordInterval) {
      clearInterval(this.recordInterval);
      this.recordInterval = null;
    }
    // 清除录制流
    this.mediaStream?.getTracks().forEach(track => track.stop());
    // 视频流清除
    this.stream?.getTracks().forEach(track => track.stop());
    // 屏幕流清除
    this.screenStream?.getTracks().forEach(track => track.stop());
    // 初始化 清空的数据
    this.mediaStream = undefined;
    this.stream = undefined;
    this.screenStream = undefined;
    this.recordStatus = '';
    this.deviceActiveStatus = '';
  }
}
